/**
 * Router guardlarını yöneten genel modül
 */
import { useContentStore } from '@/stores/content'
// import { useSocialMediaStore } from '@/stores/social-media'; // Gerektiğinde eklenebilir

// Yardımcı fonksiyonlar

/**
 * Route'un izlenenler listesinde olup olmadığını kontrol eder
 * @param {Object} to - Hedef route
 * @param {Object} from - Kaynak route
 * @param {string[]} routeNames - İzlenecek route isimleri
 * @returns {boolean} - Route'un izlenip izlenmeyeceği
 */
function shouldHandleRoute(to, from, routeNames) {
  return routeNames.includes(to.name) || routeNames.includes(from.name)
}

/**
 * Görünüm değişikliğini algılar
 * @param {Object} to - Hedef route
 * @param {Object} from - Kaynak route
 * @param {string} queryParam - İzlenecek query parametresi
 * @returns {Object|null} - Değişiklik varsa { toView, fromView } objesi, yoksa null
 */
function detectViewChange(to, from, queryParam) {
  const toView = to.query[queryParam]
  const fromView = from.query[queryParam]

  // Eğer görünüm değiştiyse
  if (toView !== fromView) {
    return { toView, fromView }
  }

  return null
}

/**
 * Store'un görünüm durumunu günceller
 * @param {Object} store - Store nesnesi
 * @param {string|undefined} toView - Hedef görünüm
 * @param {string|undefined} fromView - Kaynak görünüm
 * @param {string} detailValue - Detay görünüm değeri
 * @param {string} listValue - Liste görünüm değeri
 * @returns {string|null} - Güncellenen görünüm durumu veya null
 */
function updateViewState(store, toView, fromView, detailValue, listValue) {
  if (toView === detailValue) {
    store.lastActiveView = detailValue
    return detailValue
  } else if (fromView === detailValue && !toView) {
    store.lastActiveView = listValue
    return listValue
  }

  return null
}

/**
 * Önbellek temizleme işlemini yönetir
 * @param {Object} store - Store nesnesi
 * @param {string} currentView - Mevcut görünüm durumu
 * @param {string} listValue - Liste görünüm değeri
 * @param {Function} invalidateListCache - Özel önbellek temizleme fonksiyonu
 */
function invalidateCache(store, currentView, listValue, invalidateListCache) {
  // Sadece liste görünümüne dönülduyse önbelleği temizle
  if (currentView !== listValue) return

  // Çeşitli önbellek temizleme metotlarını dene
  if (typeof store._invalidateRelatedCaches === 'function') {
    store._invalidateRelatedCaches()
  } else if (typeof store._invalidateContentListCache === 'function') {
    store._invalidateContentListCache()
  } else if (invalidateListCache && typeof invalidateListCache === 'function') {
    invalidateListCache(store)
  }
}

/**
 * Router için view değişikliklerini izleyen ve ilgili önbellekleri temizleyen guardı oluşturur
 * @param {Object} options - Guard seçenekleri
 * @param {string[]} options.routeNames - İzlenecek route isimleri
 * @param {string} options.queryParam - İzlenecek query parametresi (genellikle 'view')
 * @param {Function} options.getStore - İlgili store'u döndüren fonksiyon (örn: () => useContentStore())
 * @param {string} options.detailValue - Detail view değerini belirten string (genellikle 'detail')
 * @param {string} options.listValue - List view değerini belirten string (genellikle 'list')
 * @param {Function} options.invalidateListCache - Liste önbelleğini temizleyen fonksiyon
 * @returns {Function} - Route guard fonksiyonu
 */
export function createViewTracker(options) {
  const {
    routeNames = [],
    queryParam = 'view',
    getStore,
    detailValue = 'detail',
    listValue = 'list',
    invalidateListCache = null,
  } = options

  // Router afterEach hook'u için guard fonksiyonu
  return (to, from) => {
    // Route kontrolü
    if (!shouldHandleRoute(to, from, routeNames)) {
      return
    }

    // İlgili store'u al
    const store = getStore()
    if (!store) return

    // Görünüm değişikliğini algıla
    const viewChange = detectViewChange(to, from, queryParam)
    if (!viewChange) return

    // Görünüm durumunu güncelle
    const { toView, fromView } = viewChange
    const currentView = updateViewState(store, toView, fromView, detailValue, listValue)

    // Önbelleği temizle
    if (currentView) {
      invalidateCache(store, currentView, listValue, invalidateListCache)
    }
  }
}

/**
 * Content modülü için görünüm değişikliği izleyici oluşturur
 * @param {boolean} resetOnExternalNavigation - Başka sayfadan gelince state'i sıfırla (varsayılan: false)
 * @returns {Function} - Router guard fonksiyonu
 */
export function createContentViewTracker(resetOnExternalNavigation = false) {
  let lastPath = '' // Son ziyaret edilen path

  return (to, from) => {
    const contentRelatedPaths = ['/content-moderator']
    const isContentPage = contentRelatedPaths.some(path => to.path.startsWith(path))
    const wasOnContentPage = contentRelatedPaths.some(path => from.path.startsWith(path))
    const isExternalNavigation = isContentPage && !wasOnContentPage

    // Content sayfasına başka bir yerden geliyorsa, resetOnExternalNavigation true ise state'i sıfırla
    if (isExternalNavigation && resetOnExternalNavigation) {
      const contentStore = useContentStore()
      if (contentStore) {
        // Sayfa durumunu sıfırla, ancak filtreyi koru
        contentStore.resetPageState(true)

        if (process.env.NODE_ENV === 'development') {
          console.log('Dış navigasyon: Content sayfasına başka yerden giriş, state sıfırlandı')
        }
      }
    }

    // Görünüm değişikliklerini izleme
    return createViewTracker({
      routeNames: ['contentModerator'],
      queryParam: 'view',
      getStore: () => useContentStore(),
      detailValue: 'detail',
      listValue: 'list',
      invalidateListCache: (store) => store._invalidateContentListCache(),
    })(to, from)
  }
}

/**
 * Router'a gerekli tüm guard'ları ekler
 * @param {Object} router - Vue Router nesnesi
 * @param {boolean} resetContentStateOnNavigation - Content sayfasına başka yerden gelince state'i sıfırla
 */
export function setupRouterGuards(router, resetContentStateOnNavigation = false) {
  // Content modülü için view değişikliklerini izle
  router.afterEach(createContentViewTracker(resetContentStateOnNavigation))

  // Diğer modüller için buraya ek guardlar eklenebilir
  // router.afterEach(createSocialMediaViewTracker());
}

export default {
  setupRouterGuards,
  createViewTracker,
  createContentViewTracker,
}